import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  enableProdMode,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  Provider,
  provideZonelessChangeDetection
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { ToastrModule } from 'ngx-toastr';

import { APP_PROVIDERS } from '@app/app.providers';
import { APP_ROUTES } from '@app/app.routes';
import { AppInitializerService } from '@app/shared/services/app-initializer.service';
import { AUTH_PROVIDERS, MSAL_INTERCEPTOR } from '@auth/auth.providers';
import { CORE_PROVIDERS } from '@core/core.providers';
import { CLIENT_INTERCEPTOR } from '@core/interceptor/client.interceptor';
import { UNAUTHORIZED_INTERCEPTOR } from '@core/interceptor/unauthorized.interceptor';
import { CORE_INTERACTIONS_PROVIDERS } from '@core-interactions/core-interactions.providers';
import { POWER_BI_PROVIDERS } from '@power-bi/power-bi.providers';
import { MATERIAL_PROVIDERS } from '@shared/material/material.providers';
import { SVG_ICONS_PROVIDERS } from '@shared/svg-icons/svg-icons.providers';
import { STORE_PROVIDERS } from '@stores/providers/store.providers';
import { ZENDESK_PROVIDERS } from '@zendesk/zendesk.providers';

interface Environment {
  production: boolean;
}

export function getAppProviders(
  environment: Environment
): (EnvironmentProviders | Provider)[] {
  if (environment.production) {
    enableProdMode();
  }

  return [
    provideRouter(APP_ROUTES),
    importProvidersFrom(
      BrowserModule,
      ToastrModule.forRoot({
        positionClass: 'toast-bottom-right',
        tapToDismiss: true,
        enableHtml: true
      })
    ),
    provideAppInitializer(() => inject(AppInitializerService).init()),
    // ! Angular animations won't be long term
    provideAnimationsAsync(),
    provideZonelessChangeDetection(),
    provideHttpClient(
      withInterceptors([
        MSAL_INTERCEPTOR,
        CLIENT_INTERCEPTOR,
        UNAUTHORIZED_INTERCEPTOR
      ]),
      withInterceptorsFromDi()
    ),
    ...APP_PROVIDERS,
    ...AUTH_PROVIDERS,
    ...MATERIAL_PROVIDERS,
    ...POWER_BI_PROVIDERS,
    ...CORE_PROVIDERS,
    ...CORE_INTERACTIONS_PROVIDERS,
    ...STORE_PROVIDERS,
    ...SVG_ICONS_PROVIDERS,
    ...ZENDESK_PROVIDERS
  ];
}
